/**
 * The permission catalogue, fetched once (FS-007b).
 *
 * Its own file rather than living beside the chip grid, because a module that
 * exports both a component and a hook breaks Vite's fast refresh — and because
 * the catalogue is the backend registry's shape, which other screens will want
 * (FS-008's route guards, FS-035's access explainer) without wanting the
 * picker.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";

export interface PermissionDeclaration {
  label: string;
  group: string;
  sensitivity: "routine" | "elevated" | "critical";
  implies: string[];
}

export interface PermissionCatalogue {
  /** Group name → permission keys, in the order the registry declares them. */
  groups: Record<string, string[]>;
  catalogue: Record<string, PermissionDeclaration>;
}

/**
 * One query for the catalogue, shared by every caller.
 *
 * The key is deliberately the same in all of them so TanStack serves one
 * request; the catalogue changes when the code changes, not at runtime.
 */
export function usePermissionCatalogue() {
  const { data } = useQuery<PermissionCatalogue>({
    queryKey: ["all-permissions"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/permissions/all-permissions")).data,
    staleTime: Infinity,
  });
  return {
    groups: data?.groups ?? {},
    catalogue: data?.catalogue ?? {},
  };
}
